import {
  Component,
  ChangeDetectionStrategy,
  ViewEncapsulation,
  OnInit,
  inject,
  computed,
  DOCUMENT,
  effect,
} from '@angular/core';
import { SwUpdate } from '@angular/service-worker';
import { Router, RouterOutlet } from '@angular/router';
import { Dialog, DialogRef } from '@angular/cdk/dialog';
import { AuthService, ConfigService } from '@assist/shared/data';
import { MultiviewComponent, MultiviewRenewComponent } from '@assist/multiview';
import type { MultiViewData, MultiViewRenewData } from '@assist/multiview';
import { FaviconService } from '@cue/utilities';
import { SignalRService } from '@cue/api';
import { injectDestroy } from 'ngxtension/inject-destroy';
import { injectQueryParams } from 'ngxtension/inject-query-params';
import { takeUntil, tap } from 'rxjs';
import { Actions, ofType } from '@ngrx/effects';
import * as EventsActions from '@assist/shared/data';
import { NotificationService } from '@assist/notifications/services';
import { translation } from '@cue/translations';
import { TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'cue-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [RouterOutlet],
})
export class AppComponent implements OnInit {
  private swUpdate = inject(SwUpdate);
  configService = inject(ConfigService);
  faviconService = inject(FaviconService);
  signalRService = inject(SignalRService);
  authService = inject(AuthService);
  document = inject(DOCUMENT);
  terminator = injectDestroy();
  actions = inject(Actions);
  notificationsService = inject(NotificationService);
  translocoService = inject(TranslocoService);
  private readonly dialog = inject(Dialog);
  private readonly router = inject(Router);
  private readonly multiview = injectQueryParams('multiview');
  private readonly multiviewQr = injectQueryParams('multiviewQr');
  private readonly multiviewResource = injectQueryParams('multiviewResource');
  private multiviewDialogRef: DialogRef<void, MultiviewComponent> | null = null;
  private multiviewRenewDialogRef: DialogRef<void, MultiviewRenewComponent> | null = null;

  readonly handleMultiviewQueryParams = effect(() => {
    const multiview = this.multiview();
    const multiviewQr = this.multiviewQr();
    const multiviewResource = this.multiviewResource();
    const hasRenewSource = Boolean(multiviewQr || multiviewResource);

    if (hasRenewSource && !this.multiviewRenewDialogRef) {
      this.openMultiviewRenew();
    } else if (!hasRenewSource && this.multiviewRenewDialogRef) {
      this.multiviewRenewDialogRef.close();
    }

    if (multiview && !hasRenewSource && !this.multiviewDialogRef) {
      this.openMultiview();
    } else if ((!multiview || hasRenewSource) && this.multiviewDialogRef) {
      this.multiviewDialogRef.close();
    }
  });

  themeVariables = computed(() => {
    const theme = this.configService.value.theme;
    return {
      '--assist-theme-menu-background': theme?.menuBackground,
      '--assist-theme-primary': theme?.primary,
      '--assist-theme-secondary': theme?.secondary,
      '--assist-theme-primary-text': theme?.primaryText,
      '--assist-theme-secondary-text': theme?.secondaryText,
      '--assist-theme-info': theme?.info,
      '--assist-theme-success': theme?.success,
      '--assist-theme-warning': theme?.warning,
      '--assist-theme-error': theme?.error,

      '--assist-theme-menu-unactive-text': theme?.menuBackground
        ? this.getTextColorBasedOnBg(theme?.menuBackground)
        : '',
      '--assist-theme-menu-item-active-background': theme?.menuBackground
        ? this.darkenColor(theme?.menuBackground, 10)
        : '',
      '--assist-theme-menu-hover-bg': theme?.menuBackground ? this.getHoverColorBasedOnBg(theme?.menuBackground) : '',
      '--menu-logo-filter': this.isLightColor(theme?.menuBackground)
        ? 'brightness(1) invert(0)'
        : 'brightness(0) invert(1)',
    };
  });

  appendVariablesToBody() {
    const setVariableKeys = Object.keys(this.themeVariables()).filter((key) => this.themeVariables()[key]);
    if (setVariableKeys.length === 0) return;
    const code = setVariableKeys.reduce((acc, it) => {
      return acc + `${it}: ${this.themeVariables()[it]};\n`;
    }, '');

    const style = this.document.createElement('style');
    style.type = 'text/css';
    style.innerHTML = `
      :root {
        ${code}
      }
    `;
    this.document.head.appendChild(style);
  }

  ngOnInit() {
    this.actions
      .pipe(
        takeUntil(this.terminator),
        ofType(EventsActions.crateEventError),
        tap((action) => {
          this.notificationsService.error(
            this.translocoService.translate(translation.reservations.errorCreateEvent),
            action.error
          );
        })
      )
      .subscribe();
    this.signalRService.startConnection('/MessageHub');

    this.authService.apiTokenChanged
      .pipe(
        takeUntil(this.terminator),
        tap(() => this.signalRService.restartConnection('/MessageHub'))
      )
      .subscribe();

    this.appendVariablesToBody();
    if (this.configService.value.favIconUrl) {
      this.faviconService.setFavicon(this.configService.value.favIconUrl + '&format=png&width=96&height=96');
    }

    if (this.swUpdate.isEnabled) {
      this.swUpdate.versionUpdates.subscribe((evt) => {
        switch (evt.type) {
          case 'VERSION_DETECTED':
            console.log(`Downloading new app version: ${evt.version.hash}`);
            break;
          case 'VERSION_READY':
            if (confirm('New version available. Load New Version?')) {
              window.location.reload();
            }
            break;
          case 'VERSION_INSTALLATION_FAILED':
            console.log(`Failed to install app version '${evt.version.hash}': ${evt.error}`);
            break;
        }
      });
    }
  }

  getTextColorBasedOnBg(color?: string): string {
    if (!color) return '#212529';
    const [r, g, b] = color.match(/\d+/g).map(Number);
    const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;
    return luminance > 128 ? '#212529' : '#ffffff';
  }

  getHoverColorBasedOnBg(color?: string): string {
    if (!color) return 'rgba(0,0,0,.15)';
    const [r, g, b] = color.match(/\d+/g).map(Number);
    const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;
    return luminance > 128 ? 'rgba(0,0,0,.15)' : 'rgba(255,255,255,.3)';
  }

  darkenColor(color: string, percent: number): string {
    const rgbaRegex = /rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)/;
    const match = color.match(rgbaRegex);

    if (!match) {
      console.warn('Invalid RGBA color format:', color);
      return color;
    }

    const r = Math.max(0, Math.min(255, parseInt(match[1]) - (percent / 100) * 255));
    const g = Math.max(0, Math.min(255, parseInt(match[2]) - (percent / 100) * 255));
    const b = Math.max(0, Math.min(255, parseInt(match[3]) - (percent / 100) * 255));
    const a = match[4] !== undefined ? parseFloat(match[4]) : 1;

    return `rgba(${Math.round(r)}, ${Math.round(g)}, ${Math.round(b)}, ${a})`;
  }

  isLightColor(color?: string): boolean {
    if (!color) return false;

    const [r, g, b] = color.match(/\d+/g)?.map(Number) ?? [0, 0, 0];
    const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;

    return luminance > 128;
  }

  private openMultiview(): void {
    const dialogRef = this.dialog.open<void, MultiViewData, MultiviewComponent>(MultiviewComponent, {
      data: {
        mode: this.getMultiviewMode(),
      },
    });
    this.multiviewDialogRef = dialogRef;
    dialogRef.closed.subscribe(() => {
      if (this.multiviewDialogRef === dialogRef) {
        this.multiviewDialogRef = null;
        this.clearMultiviewQueryParams({ multiview: null });
      }
    });
  }

  private openMultiviewRenew(): void {
    const dialogRef = this.dialog.open<void, MultiViewRenewData, MultiviewRenewComponent>(MultiviewRenewComponent, {
      data: {
        mode: this.getMultiviewMode(),
      },
    });
    this.multiviewRenewDialogRef = dialogRef;
    dialogRef.closed.subscribe(() => {
      if (this.multiviewRenewDialogRef === dialogRef) {
        this.multiviewRenewDialogRef = null;
        this.clearMultiviewQueryParams({
          multiviewQr: null,
          multiviewResource: null,
          multiviewQrScannedAt: null,
        });
      }
    });
  }

  private getMultiviewMode(): MultiViewData['mode'] {
    return this.authService.getApiToken() == null ? 'anonymous' : 'authenticated';
  }

  private clearMultiviewQueryParams(queryParams: Record<string, null>): void {
    void this.router.navigate([], {
      queryParams,
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }
}
